import { groupBy, uniq } from 'lodash'
import {
  BACK_OFFICE_LABEL_OVERRIDES,
  KNOWN_TICKET_FIELDS,
  REQUIRED_BEFORE_RECEIVED,
  SEARCHABLE,
  SENSITIVE,
} from './claim-form-constants'
import { formatLabel } from './claim-form-utils'

/**
 * Everything the drawer reads out of the claim form JSON Schema PDS serves: which fields to render,
 * what to call them, and which agent-entered values are still outstanding.
 *
 * Kept apart from claim-form-utils (which formats values) and claim-form-constants (which holds the
 * static layout tables) so schema traversal has one home rather than being split across both.
 */

/**
 * Walks a JSON Schema to a nested node. Path segments address properties by name, except 'items',
 * which steps into an array's item schema — so ['tickets', 'items'] reaches a ticket's own fields.
 * A property literally named `items` is therefore unaddressable; every caller uses a fixed path.
 */
export function getSchemaNode(
  schema: Record<string, unknown> | undefined,
  path: string[],
): Record<string, unknown> | undefined {
  let node = schema
  for (const segment of path) {
    if (!node) {
      return undefined
    }
    if (segment === 'items') {
      node = node.items as Record<string, unknown> | undefined
    } else {
      const properties = node.properties as Record<string, unknown> | undefined
      node = properties?.[segment] as Record<string, unknown> | undefined
    }
  }
  return node
}

/** Ticket item properties beyond the three the drawer renders explicitly, in schema order. */
export function getExtraTicketProperties(
  schema?: Record<string, unknown>,
): [string, Record<string, unknown>][] {
  const items = getSchemaNode(schema, ['tickets', 'items'])
  const properties = (items?.properties ?? {}) as Record<string, Record<string, unknown>>
  return Object.entries(properties).filter(([key]) => !KNOWN_TICKET_FIELDS.has(key))
}

/**
 * Whether a ticket field shows a required marker. Agent-entered fields are optional in the schema so
 * a claimant can submit without them, so the x-required-before-received flag is what marks them.
 */
export function isTicketFieldRequired(
  fieldKey: string,
  property: Record<string, unknown>,
  schema?: Record<string, unknown>,
): boolean {
  const items = getSchemaNode(schema, ['tickets', 'items'])
  const required = (items?.required ?? []) as string[]
  return required.includes(fieldKey) || property[REQUIRED_BEFORE_RECEIVED] === true
}

/**
 * Get a display label for a field. Checks back-office overrides first, then the schema label, then
 * falls back to formatting the field key. `sectionPath` may be a section name or a nested path such
 * as ['tickets', 'items'].
 */
export function getFieldLabel(
  fieldKey: string,
  schema?: Record<string, unknown>,
  sectionPath?: string | string[],
): string {
  if (BACK_OFFICE_LABEL_OVERRIDES[fieldKey]) {
    return BACK_OFFICE_LABEL_OVERRIDES[fieldKey]
  }

  if (schema && sectionPath) {
    const path = Array.isArray(sectionPath) ? sectionPath : [sectionPath]
    const section = getSchemaNode(schema, path)
    const sectionProps = section?.properties as Record<string, unknown> | undefined
    const field = sectionProps?.[fieldKey] as Record<string, unknown> | undefined
    if (field?.label) {
      return field.label as string
    }
  }

  // Fallback: convert camelCase to Title Case
  return formatLabel(fieldKey)
}

function isEmptySchemaValue(value: unknown): boolean {
  if (typeof value === 'string') {
    return value.trim() === ''
  }
  return value === null || value === undefined
}

function schemaFieldLabel(fieldKey: string, property: Record<string, unknown>): string {
  if (typeof property.label === 'string') {
    return property.label
  }
  return formatLabel(fieldKey)
}

/** 'tickets' + index 2 → 'Ticket 3', matching the numbering the agent sees in the ticket carousel. */
function arrayEntryLabel(sectionKey: string, index: number): string {
  const singular = sectionKey.replace(/s$/, '')
  return `${formatLabel(singular)} ${index + 1}`
}

/**
 * A value the received gate requires that is still empty, in the terms an agent reads it: the submit
 * gate lists them one by one, and each section's header warns about its own.
 */
export type MissingPreReceiptValue = {
  /** Section the value belongs to, keyed as the schema keys it (e.g. 'tickets'). */
  sectionKey: string
  /** Label of the field itself, e.g. 'Aurora ID'. */
  fieldLabel: string
  /** Set only for array sections: which entry is missing it, e.g. 'Ticket 2'. */
  entryLabel?: string
}

/**
 * Mirrors the PDS received gate: values the schema flags x-required-before-received that are still
 * empty, labelled the way an agent reads them ('Ticket 2: Aurora ID') so the copy matches the
 * server's rejection message. Advisory only — the server rejects the transition regardless.
 *
 * Only top-level sections, their immediate fields, and array-item fields are inspected. That covers
 * every place the flag is used today; a flag nested deeper would be silently not gated here, so
 * extend this (and the PDS walker it mirrors) if the schema grows one.
 */
export function findMissingPreReceiptValues(
  schema: Record<string, unknown> | undefined,
  formData: Record<string, unknown> | undefined,
): MissingPreReceiptValue[] {
  if (!schema || !formData) {
    return []
  }

  const missing: MissingPreReceiptValue[] = []
  const properties = (schema.properties ?? {}) as Record<string, Record<string, unknown>>

  for (const [sectionKey, sectionSchema] of Object.entries(properties)) {
    const value = formData[sectionKey]

    if (sectionSchema[REQUIRED_BEFORE_RECEIVED]) {
      if (isEmptySchemaValue(value)) {
        missing.push({ sectionKey, fieldLabel: schemaFieldLabel(sectionKey, sectionSchema) })
      }
      continue
    }

    if (sectionSchema.type === 'array') {
      const items = (sectionSchema.items ?? {}) as Record<string, unknown>
      const itemProperties = (items.properties ?? {}) as Record<string, Record<string, unknown>>
      const flagged = Object.entries(itemProperties).filter(
        ([, property]) => property[REQUIRED_BEFORE_RECEIVED],
      )
      if (flagged.length === 0 || !Array.isArray(value)) {
        continue
      }
      value.forEach((element, index) => {
        for (const [fieldKey, property] of flagged) {
          if (isEmptySchemaValue((element as Record<string, unknown>)?.[fieldKey])) {
            missing.push({
              sectionKey,
              fieldLabel: schemaFieldLabel(fieldKey, property),
              entryLabel: arrayEntryLabel(sectionKey, index),
            })
          }
        }
      })
      continue
    }

    const sectionProperties = (sectionSchema.properties ?? {}) as Record<
      string,
      Record<string, unknown>
    >
    for (const [fieldKey, property] of Object.entries(sectionProperties)) {
      if (
        property[REQUIRED_BEFORE_RECEIVED] &&
        isEmptySchemaValue((value as Record<string, unknown>)?.[fieldKey])
      ) {
        missing.push({ sectionKey, fieldLabel: schemaFieldLabel(fieldKey, property) })
      }
    }
  }

  return missing
}

/** A schema-flagged searchable field: `label` names it to an agent, `key` reads its values off a result. */
export type SearchableField = { key: string; label: string }

/**
 * The fields the customer's schema flags searchable: their labels name what an agent can type in the
 * search copy, and their keys read the matching values off a search result, which PDS returns keyed the
 * same way. The flagged fields vary by customer, and only the schema knows them.
 *
 * Bounded the same way findMissingPreReceiptValues is: top-level sections, their immediate fields, and
 * array-item fields, mirroring the PDS walker that populates the columns being searched and shown.
 *
 * A field flagged sensitive is skipped even when it is also flagged searchable, because PDS withholds it
 * from the plaintext search columns — offering it here would name a field the search cannot match.
 * Read from the field itself, not inherited from its section, exactly as PDS reads it.
 */
export function getSearchableFields(
  schema: Record<string, unknown> | undefined,
): SearchableField[] {
  if (!schema) {
    return []
  }

  const fields: SearchableField[] = []
  const properties = (schema.properties ?? {}) as Record<string, Record<string, unknown>>

  const isSearchable = (property: Record<string, unknown>): boolean => {
    return property[SEARCHABLE] === true && property[SENSITIVE] !== true
  }

  for (const [sectionKey, sectionSchema] of Object.entries(properties)) {
    if (sectionSchema[SEARCHABLE]) {
      if (isSearchable(sectionSchema)) {
        fields.push({ key: sectionKey, label: schemaFieldLabel(sectionKey, sectionSchema) })
      }
      continue
    }

    if (sectionSchema.type === 'array') {
      const items = (sectionSchema.items ?? {}) as Record<string, unknown>
      const itemProperties = (items.properties ?? {}) as Record<string, Record<string, unknown>>
      for (const [fieldKey, property] of Object.entries(itemProperties)) {
        if (isSearchable(property)) {
          // One entry per field, not per ticket — PDS keys every ticket's value under the same field.
          fields.push({ key: fieldKey, label: schemaFieldLabel(fieldKey, property) })
        }
      }
      continue
    }

    const sectionProperties = (sectionSchema.properties ?? {}) as Record<
      string,
      Record<string, unknown>
    >
    for (const [fieldKey, property] of Object.entries(sectionProperties)) {
      if (isSearchable(property)) {
        fields.push({ key: fieldKey, label: schemaFieldLabel(fieldKey, property) })
      }
    }
  }

  return fields
}

/**
 * The field list in the search copy: the terms that are always searchable plus whatever the schema
 * flags. Base terms are hardcoded because the name and contact columns the search matches are found by
 * fixed key, not by flag — flagging them would make PDS copy them into the searchable column too.
 */
export function formatSearchFieldList(baseTerms: string[], schemaLabels: string[]): string {
  const terms = [...baseTerms, ...schemaLabels]
  if (terms.length < 3) {
    return terms.join(' or ')
  }
  return `${terms.slice(0, -1).join(', ')}, or ${terms[terms.length - 1]}`
}

/** 'Ticket 2: Aurora ID' for an array entry's value, the field's own label otherwise. */
function describeMissingValue({ entryLabel, fieldLabel }: MissingPreReceiptValue): string {
  if (!entryLabel) {
    return fieldLabel
  }
  return `${entryLabel}: ${fieldLabel}`
}

/** Agent-facing explanation of why the received button is disabled. */
export function formatMissingPreReceiptMessage(missing: MissingPreReceiptValue[]): string {
  const described = missing.map(describeMissingValue)
  if (described.length <= 3) {
    return `Cannot submit until these are filled: ${described.join(', ')}`
  }
  return `Cannot submit until ${described.length} values are filled, starting with ${described.slice(0, 3).join(', ')}`
}

/** 'an Aurora ID', or 'an Aurora ID and a Case Number' when a section is missing several. */
function formatMissingLabelList(labels: string[]): string {
  const withArticles = labels.map((label) => {
    const article = /^[aeiou]/i.test(label) ? 'an' : 'a'
    return `${article} ${label}`
  })
  if (withArticles.length < 3) {
    return withArticles.join(' and ')
  }
  return `${withArticles.slice(0, -1).join(', ')}, and ${withArticles[withArticles.length - 1]}`
}

/**
 * Warning copy per section, keyed by section, so a section header can say what it is still missing and
 * an agent knows which card to open without reading the whole submit-gate list.
 */
export function formatSectionWarnings(missing: MissingPreReceiptValue[]): Record<string, string> {
  const warnings: Record<string, string> = {}

  for (const [sectionKey, values] of Object.entries(groupBy(missing, 'sectionKey'))) {
    const labels = formatMissingLabelList(uniq(values.map((value) => value.fieldLabel)))
    if (values.some((value) => value.entryLabel)) {
      // Singular ('One or more ticket…') because any one entry in the section can be the one missing it,
      // and naming which would just repeat the submit gate's list.
      const entryName = formatLabel(sectionKey.replace(/s$/, '')).toLowerCase()
      warnings[sectionKey] = `One or more ${entryName} is missing ${labels}`
    } else {
      warnings[sectionKey] = `Missing ${labels}`
    }
  }

  return warnings
}
